// Pixie's three ways in: the greeting cards in her empty state and on the
// banner pixie.js draws on a publisher's page. Tapping one sends its `prompt`
// with its `key` as the chat's pathway (arcs-api records it as an opener).

export type PixiePathway = "browsing" | "deciding" | "planning";

export interface PixieWayIn {
  key: PixiePathway;
  title: string;
  subtitle: string;
  prompt: string;
}

export const PIXIE_WAYS_IN: PixieWayIn[] = [
  {
    key: "browsing",
    title: "Just browsing",
    subtitle: "I'm looking for inspiration.",
    prompt: "I'm just browsing — show me some inspiration.",
  },
  {
    key: "deciding",
    title: "Deciding",
    subtitle: "I'm working out if this recipe is right for me.",
    prompt: "Help me work out if this recipe is right for me.",
  },
  {
    key: "planning",
    title: "Planning",
    subtitle: "I'm planning a meal, occasion or something coming up.",
    prompt: "Help me plan a meal, occasion or something coming up.",
  },
];

/** The starters edge's copy for the three cards (`askTypes.ts`'s `StarterSet`). */
export interface PixieStarterCopy {
  prompts: string[];
  titles: string[] | null;
  subtitles: string[] | null;
}

// Off: every card shows the copy above, whatever the starters edge sends (the
// panel still fetches it). That copy is written for one viewer's taste
// profile, keyed by the play host's device key, which a publisher's page
// cannot read. So the banner always passes null, and turning this on without
// handing the panel's copy to the banner would make a banner card and its
// panel card say different things.
export const SERVER_STARTERS = false;

/**
 * The three ways in, with the starters edge's copy over them when
 * `SERVER_STARTERS` is on and it sent titles. Keys stay, so pathways do too.
 */
export function pixieWaysIn(starters: PixieStarterCopy | null): PixieWayIn[] {
  // A prompts-only body (older edge) is ignored so we do not pair
  // "Just browsing" with a pasta opener.
  if (
    !SERVER_STARTERS ||
    !starters ||
    !starters.titles ||
    starters.titles.length !== 3
  ) {
    return PIXIE_WAYS_IN;
  }
  const titles = starters.titles;
  const subtitles = starters.subtitles;
  return PIXIE_WAYS_IN.map((way, index) => {
    const prompt = starters.prompts[index];
    return {
      key: way.key,
      title: titles[index],
      subtitle: subtitles && subtitles[index] ? subtitles[index] : prompt,
      prompt: prompt,
    };
  });
}

/** The pathway, or null for anything else (the chat edge allowlists these three). */
export function readPathway(value: unknown): PixiePathway | null {
  return value === "browsing" || value === "deciding" || value === "planning"
    ? value
    : null;
}
